import { api } from '@/lib/api';
import type {
  UserAttributeDefinition,
  UserAttributeValue,
  UserAttributesRecord,
} from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';

interface Props {
  value: UserAttributesRecord;
  onChange: (next: UserAttributesRecord) => void;
}

/**
 * 사용자 등록/편집 폼에서 동적 항목을 그려준다.
 * 정의가 0개면 자체적으로 null 을 반환 (폼 공간 차지 안 함).
 */
export function UserAttributesEditor({ value, onChange }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user-attributes'],
    queryFn: api.adminUserAttributesList,
  });
  const defs = data?.definitions ?? [];

  if (isLoading) {
    return <div className="text-sm text-foreground-muted">사용자 항목 정의를 불러오는 중…</div>;
  }
  if (defs.length === 0) return null;

  const setFor = (key: string, v: UserAttributeValue) => {
    const next = { ...value };
    if (v === null || v === '') {
      delete next[key];
    } else {
      next[key] = v;
    }
    onChange(next);
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      {defs.map((def) => (
        <AttributeInput
          key={def.id}
          def={def}
          value={value[def.key]}
          onChange={(v) => setFor(def.key, v)}
        />
      ))}
    </div>
  );
}

function AttributeInput({
  def,
  value,
  onChange,
}: {
  def: UserAttributeDefinition;
  value: UserAttributeValue | undefined;
  onChange: (v: UserAttributeValue) => void;
}) {
  const required = def.required;
  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {def.label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
        <span className="ml-2 font-mono text-[10px] text-foreground-muted">{def.key}</span>
      </span>
      <FieldFor def={def} value={value} onChange={onChange} />
    </div>
  );
}

function FieldFor({
  def,
  value,
  onChange,
}: {
  def: UserAttributeDefinition;
  value: UserAttributeValue | undefined;
  onChange: (v: UserAttributeValue) => void;
}) {
  switch (def.type) {
    case 'text':
      return (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') onChange(null);
            else {
              const n = Number(v);
              onChange(Number.isFinite(n) ? n : null);
            }
          }}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={
            typeof value === 'string' && value ? new Date(value).toISOString().slice(0, 10) : ''
          }
          onChange={(e) => {
            const v = e.target.value;
            onChange(v ? new Date(`${v}T00:00:00Z`).toISOString() : null);
          }}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
        />
      );
    case 'boolean':
      return (
        <div className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            className="size-4"
          />
          <span className="text-foreground-muted">{value === true ? '예' : '아니오'}</span>
        </div>
      );
    case 'enum': {
      const options = def.options ?? [];
      return (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
        >
          <option value="">선택 안 함</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
  }
}
